import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Home, Building2, Shield, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export default function WhyChoose() {
  const scrollToForm = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const services = [
    {
      icon: <Home className="h-10 w-10 sm:h-12 sm:w-12" />,
      title: "Residential Roofing",
      description: "Whether your roof is leaking or your home has been damaged by storms, Elite Roofing is fully certified and capable of restoring your home back to pristine condition quickly and efficiently.",
      link: "/residential-services",
      isExternal: false
    },
    {
      icon: <Building2 className="h-10 w-10 sm:h-12 sm:w-12" />,
      title: "Commercial Roofing",
      description: "Elite Roofing provides commercial roof restoration services throughout Central Florida. We work with your schedule, maintaining courtesy and respect for your space and time.",
      link: "/commercial-services",
      isExternal: false
    },
    {
      icon: <Shield className="h-10 w-10 sm:h-12 sm:w-12" />,
      title: "Insurance Claims",
      description: "We provide comprehensive documentation and assessment reports. Use our detailed inspection findings to expedite the process related to your insurance claim.",
      link: "/insurance-claims",
      isExternal: false
    }
  ];

  const handleClick = (service) => {
    if (service.isExternal) {
      scrollToForm();
    }
  };

  return (
    <section className="bg-slate-50 py-12 sm:py-16 md:py-24" id="services">
      <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-3 sm:mb-4">
            Why Choose Elite Roofing As Your
          </h2>
          <p className="text-xl sm:text-2xl md:text-3xl text-[hsl(var(--primary))] font-bold">
            Roofing & Exterior Company?
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 hover:border-[hsl(var(--primary))] group">
                <CardHeader className="p-5 sm:p-6">
                  <div className="mb-4 text-[hsl(var(--accent))] group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <CardTitle className="text-xl sm:text-2xl mb-3">{service.title}</CardTitle>
                  <CardDescription className="text-sm sm:text-base text-slate-600 leading-relaxed">
                    {service.description}
                  </CardDescription>
                  {service.isExternal ? (
                    <Button 
                      variant="link" 
                      className="text-[hsl(var(--primary))] font-semibold p-0 h-auto hover:gap-3 transition-all group/btn mt-3"
                      onClick={() => handleClick(service)}
                    >
                      Learn more 
                      <ArrowRight className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  ) : (
                    <Link 
                      to={service.link}
                      className="inline-flex items-center text-[hsl(var(--primary))] font-semibold hover:gap-3 transition-all group/btn mt-3"
                    >
                      Learn more 
                      <ArrowRight className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                  )}
                </CardHeader>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
